import React from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";

interface FieldDisplayProps {
  field: {
    name: string;
    type: string;
    mandatory: boolean;
    options?: string[];
    customFieldId: number;
    placeholder: string;
  };
  value?: any;
  error?: string;
  onChange?: (fieldId: number, value: any) => void;
}

const FieldDisplay: React.FC<FieldDisplayProps> = ({ field, value, error, onChange }) => {
  if (field.type === "dropdown") {
    return (
      <FormControl fullWidth size="small" required={field.mandatory} error={!!error}>
        <InputLabel>{field.name}</InputLabel>
        <Select
          label={field.name}
          value={value ?? ""}
          onChange={e => onChange && onChange(field.customFieldId, e.target.value)}
          autoComplete="off"
          placeholder={field.placeholder || ''}
        >
          {field.options && field.options.length > 0 ? (
            field.options.map((opt: string, idx: number) => (
              <MenuItem key={idx} value={opt}>{opt}</MenuItem>
            ))
          ) : (
            <MenuItem value="">No options</MenuItem>
          )}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  if (field.type === "boolean") {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FormControlLabel
          control={<Checkbox
            checked={value === 'True' || false}
            onChange={e => onChange && onChange(field.customFieldId, e.target.checked ? 'True' : 'False')}
          />}
          label={field.name}
          sx={{ ml: 1, ...(field.mandatory ? { color: '#B42318' } : {}) }}
          required={field.mandatory}
        />
        {error && <FormHelperText>{error}</FormHelperText>}
      </div>
    );
  }

  if (field.type === "date") {
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

    return (
      <TextField
        label={field.name}
        value={value || ""}
        error={!!error}
        helperText={error}
        type="date"
        InputLabelProps={{ shrink: true }}
        fullWidth
        size="small"
        required={field.mandatory}
        inputProps={
          field.name.toLowerCase() === "date of birth"
            ? { max: today } // ✅ prevent future dates
            : {}
        }
        onChange={(e) =>
          onChange && onChange(field.customFieldId, e.target.value)
        }
        autoComplete="off"
      />
    );
  }


  if (field.type === "number") {
    const isPhoneField =
      field.name?.toLowerCase().includes("phone") ||
      field.name?.toLowerCase().includes("mobile");

    return (
      <TextField
        label={field.name}
        value={value ?? ""}
        error={!!error}
        helperText={error}
        placeholder={field.placeholder || ""}
        type="text" // keep text so we can control filtering
        fullWidth
        size="small"
        required={field.mandatory}
        onChange={e => {
          let cleanedValue;
          if (isPhoneField) {
            // allow digits and +
            cleanedValue = e.target.value.replace(/[^0-9+]/g, "");
          } else {
            // digits only
            cleanedValue = e.target.value.replace(/[^0-9]/g, "");
          }
          onChange && onChange(field.customFieldId, cleanedValue);
        }}
        inputProps={{
          inputMode: "numeric",
          pattern: isPhoneField ? "[0-9+]*" : "[0-9]*",
        }}
        autoComplete="off"
      />
    );
  }




  if (field.type === "text") {
    return (
      <TextField
        label={field.name}
        value={value || ""}
        error={!!error}
        helperText={error}
        placeholder={field.placeholder || ''}
        type="text"
        fullWidth
        size="small"
        required={field.mandatory}
        onChange={e => onChange && onChange(field.customFieldId, e.target.value)}
      />
    );
  }

  return null;
};

export default FieldDisplay;
